import { inject } from '@angular/core';
import { CanActivateFn, CanLoadFn, Router, UrlTree } from '@angular/router';
import { environment } from '../environments/environment';
import { AuthSessionWatchdogService } from '../service/auth-session-watchdog.service';

/**
 * Route guard that enforces authentication on every protected route.
 *
 * Checks `sessionStorage.userToken` (tab-local) — a new browser tab has no token,
 * so pasting a deep-link always triggers a redirect to the login screen.
 *
 * The sentinel alone cannot tell a live session from a stale one (see
 * {@link AuthSessionWatchdogService}), so every admitted navigation also arms the
 * session watchdog. Arming is idempotent and a no-op once the session is confirmed,
 * so navigating between protected pages never stacks timers.
 *
 * @returns `true` when the user is authenticated, allowing navigation to proceed.
 *   When unauthenticated: in production performs a hard redirect to
 *   `environment.LOGIN_URL` (returning `false` so no Angular component renders); in
 *   development returns a `UrlTree` redirecting to `/emailVerification`.
 *
 * @side-effects In production, sets `window.location.href` directly, bypassing
 *   the Angular router to prevent the brief email-verification flash. When
 *   authenticated, arms the stale-session watchdog.
 */
export const authGuard: CanActivateFn & CanLoadFn = (): boolean | UrlTree => {
  const router = inject(Router);
  const sessionWatchdog = inject(AuthSessionWatchdogService);

  // sessionStorage.userToken is tab-local — new tabs start without it, so navigating
  // to any guarded route from a new tab will always redirect to the login screen.
  const isAuthenticated = Boolean(sessionStorage.getItem('userToken'));

  if (!isAuthenticated) {
    console.log('Access blocked: Not logged in. Redirecting to login');
    if (environment.production) {
      // Hard-redirect directly to the legacy login page so no Angular component
      // ever renders — prevents the brief emailVerification flash on new-tab navigation.
      window.location.href = environment.LOGIN_URL;
      return false;
    }
    return router.createUrlTree(['/emailVerification']);
  }

  // The sentinel passed; the watchdog now waits for proof that the session behind it is
  // usable, and falls back to the global logout if none arrives.
  sessionWatchdog.arm();
  return true;
};
